/**
 * @fileoverview Pending Account Approvals page for administrators
 * Allows admins to review and approve/reject user accounts in approval workflow
 *
 * Approval flow (single step):
 * 1. PENDING_APPROVAL_GESTAO_LENA - Gestão Lena team approval -> PENDING_TRAINING -> ACTIVE
 *
 * Note: Previously had multiple steps (Gestão Lena, Regulatório) but was simplified
 * to a single approval step. User only needs Gestão Lena role to approve.
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  intl,
  DmsPage,
  DmsAltBox,
  DmsTable,
  DmsButtonGroup,
  fetchData,
  HTTP_STATUS,
  DmsButton,
  DmsModal,
} from "dmsbasereact";
import {
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import HistoryIcon from "@mui/icons-material/History";
import WarningIcon from "@mui/icons-material/Warning";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import Swal from "sweetalert2";
import { DmsFileViewerModal } from "../../components/DmsFileViewer";
import { downloadFile } from "../../utils/fileDownload";
import { InstitutionStatusEnum } from "../../constants/InstitutionStatus";

// Constants for warning thresholds
const INACTIVE_DAYS_THRESHOLD = 15;

export const PendingAccountApprovalsPage = () => {
  const tableRef = useRef(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDocuments, setAccountDocuments] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [accountToApprove, setAccountToApprove] = useState(null);

  // Per-institution approval state
  const [userInstitutions, setUserInstitutions] = useState([]);
  const [loadingInstitutions, setLoadingInstitutions] = useState(false);
  const [selectedInstitutionForReject, setSelectedInstitutionForReject] = useState(null);
  const [rejectAllMode, setRejectAllMode] = useState(false);

  // Document preview state
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);

  // Group documents by institution
  const documentsByInstitution = useMemo(() => {
    if (!accountDocuments || accountDocuments.length === 0) return {};

    const grouped = {};
    const noInstitution = [];

    accountDocuments.forEach(doc => {
      if (doc.institution_pk && doc.institution_name) {
        const key = doc.institution_pk;
        if (!grouped[key]) {
          grouped[key] = {
            institution_name: doc.institution_name,
            institution_pk: doc.institution_pk,
            documents: []
          };
        }
        grouped[key].documents.push(doc);
      } else {
        noInstitution.push(doc);
      }
    });

    // Add documents without institution as a separate group
    if (noInstitution.length > 0) {
      grouped['_no_institution'] = {
        institution_name: null,
        institution_pk: null,
        documents: noInstitution
      };
    }

    return grouped;
  }, [accountDocuments]);

  // Check if documents have institution grouping
  const hasMultipleInstitutions = useMemo(() => {
    const keys = Object.keys(documentsByInstitution);
    return keys.length > 1 || (keys.length === 1 && keys[0] !== '_no_institution');
  }, [documentsByInstitution]);

  // Lookup map for institution statuses
  const institutionStatusMap = useMemo(() => {
    const map = {};
    userInstitutions.forEach(inst => {
      if (inst.institution_pk) {
        map[inst.institution_pk] = inst.institution_status;
      }
    });
    return map;
  }, [userInstitutions]);

  // User approval permissions
  const [approvalPermissions, setApprovalPermissions] = useState({
    can_approve_gestao_lena: false,
  });

  const refreshTable = useCallback(() => {
    if (tableRef.current) {
      tableRef.current.updateTable();
    }
  }, []);

  // Fetch user approval permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const response = await fetchData("/account/admin/my-approval-permissions", "GET", true);
        if (response.status === HTTP_STATUS.OK_200 && response.data?.data) {
          setApprovalPermissions(response.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch approval permissions:", err);
      }
    };
    fetchPermissions();
  }, []);

  const fetchAccountDocuments = useCallback(async (personPk) => {
    try {
      setLoadingDocuments(true);
      const response = await fetchData(`/account/admin/persons/${personPk}/documents`, "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        setAccountDocuments(response.data?.data || []);
      } else {
        setAccountDocuments([]);
      }
    } catch (err) {
      setAccountDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, []);

  const fetchApprovalHistory = useCallback(async (personPk) => {
    try {
      setLoadingHistory(true);
      const response = await fetchData(`/account/admin/persons/${personPk}/approval-history`, "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        setApprovalHistory(response.data?.data || []);
      } else {
        setApprovalHistory([]);
      }
    } catch (err) {
      setApprovalHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const fetchUserInstitutions = useCallback(async (personPk) => {
    try {
      setLoadingInstitutions(true);
      const response = await fetchData(
        `/account/admin/accounts/${personPk}/institutions-with-approval-status`,
        "GET",
        true
      );
      if (response.status === HTTP_STATUS.OK_200) {
        setUserInstitutions(response.data?.institutions || []);
      } else {
        setUserInstitutions([]);
      }
    } catch (err) {
      console.error("Failed to fetch institutions:", err);
      setUserInstitutions([]);
    } finally {
      setLoadingInstitutions(false);
    }
  }, []);

  const handleViewDetails = useCallback(async (account) => {
    setSelectedAccount(account);
    setDetailModalOpen(true);
    await Promise.all([
      fetchAccountDocuments(account.person_pk),
      fetchApprovalHistory(account.person_pk),
      fetchUserInstitutions(account.person_pk)
    ]);
  }, [fetchAccountDocuments, fetchApprovalHistory, fetchUserInstitutions]);

  const handleCloseDetailModal = useCallback(() => {
    setDetailModalOpen(false);
    setSelectedAccount(null);
    setAccountDocuments([]);
    setApprovalHistory([]);
    setUserInstitutions([]);
  }, []);

  const executeApproveAccount = useCallback(async () => {
    if (!accountToApprove) return;
    try {
      setActionLoading(true);

      // Use different endpoint for type requests
      let endpoint;
      if (accountToApprove.is_type_request && accountToApprove.request_pk) {
        endpoint = `/account/admin/type-requests/${accountToApprove.request_pk}/approve`;
      } else {
        endpoint = `/account/admin/accounts/${accountToApprove.person_pk}/approve`;
      }

      const response = await fetchData(endpoint, "POST", true);
      if (response.status === HTTP_STATUS.OK_200) {
        Swal.fire({
          icon: "success",
          title: intl.get("AccountApproved").d("Account Approved"),
          text: intl.get("AccountApprovedMessage").d("The account has been approved successfully."),
        });
        setApproveModalOpen(false);
        setAccountToApprove(null);
        handleCloseDetailModal();
        refreshTable();
      } else {
        // Handle error response - detail can be a string or an object
        const detail = response.data?.detail;
        let errorMessage;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail?.error) {
          errorMessage = detail.error;
        } else if (detail?.message) {
          errorMessage = detail.message;
        } else {
          throw new Error("No error message received from backend");
        }
        Swal.fire({
          icon: "error",
          title: intl.get("Error").d("Error"),
          text: errorMessage,
        });
      }
    } catch (err) {
      // Handle error response - detail can be a string or an object
      const detail = err?.response?.data?.detail;
      let errorMessage;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail?.error) {
        errorMessage = detail.error;
      } else if (detail?.message) {
        errorMessage = detail.message;
      } else {
        throw new Error("No error message received from backend");
      }
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: errorMessage,
      });
    } finally {
      setActionLoading(false);
    }
  }, [accountToApprove, refreshTable, handleCloseDetailModal]);

  const executeRejectAccount = useCallback(async (data) => {
    const { personPk, reason, isTypeRequest, requestPk } = data;
    try {
      setActionLoading(true);

      // Use different endpoint for type requests
      let endpoint;
      if (isTypeRequest && requestPk) {
        endpoint = `/account/admin/type-requests/${requestPk}/reject`;
      } else {
        endpoint = `/account/admin/accounts/${personPk}/reject`;
      }

      const response = await fetchData(endpoint, "POST", true, {
        reason: reason,
      });
      if (response.status === HTTP_STATUS.OK_200) {
        Swal.fire({
          icon: "success",
          title: intl.get("AccountRejected").d("Account Rejected"),
          text: intl.get("AccountRejectedMessage").d("The account has been rejected."),
        });
        setRejectModalOpen(false);
        handleCloseDetailModal();
        refreshTable();
      } else {
        Swal.fire({
          icon: "error",
          title: intl.get("Error").d("Error"),
          text: response.data?.detail || intl.get("FailedToRejectAccount").d("Failed to reject account"),
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: intl.get("FailedToRejectAccount").d("Failed to reject account"),
      });
    } finally {
      setActionLoading(false);
    }
  }, [refreshTable, handleCloseDetailModal]);

  const handleRejectAccount = useCallback(async () => {
    if (!rejectReason.trim()) {
      Swal.fire({
        icon: "warning",
        title: intl.get("ReasonRequired").d("Reason Required"),
        text: intl.get("PleaseProvideRejectionReason").d("Please provide a reason for rejection."),
      });
      return;
    }

    try {
      setActionLoading(true);
      let response;

      if (rejectAllMode) {
        // Bulk reject all institutions
        response = await fetchData(
          `/account/admin/accounts/${selectedAccount.person_pk}/reject-all-institutions`,
          "POST",
          true,
          { reason: rejectReason }
        );
      } else if (selectedInstitutionForReject) {
        // Reject at specific institution
        response = await fetchData(
          `/account/admin/accounts/${selectedAccount.person_pk}/institutions/${selectedInstitutionForReject}/reject`,
          "POST",
          true,
          { reason: rejectReason }
        );
      } else {
        // Fallback to old system-wide rejection
        await executeRejectAccount({
          personPk: selectedAccount.person_pk,
          reason: rejectReason,
          isTypeRequest: selectedAccount.is_type_request,
          requestPk: selectedAccount.request_pk
        });
        return;
      }

      if (response.status === HTTP_STATUS.OK_200) {
        Swal.fire({
          icon: "success",
          title: intl.get("Rejected").d("Rejected"),
          text: rejectAllMode
            ? intl.get("RejectedAllInstitutions").d("User rejected at all institutions")
            : intl.get("RejectedAtInstitution").d("User rejected at institution"),
        });
        setRejectModalOpen(false);
        setSelectedInstitutionForReject(null);
        setRejectAllMode(false);
        handleCloseDetailModal();
        refreshTable();
      } else {
        Swal.fire({
          icon: "error",
          title: intl.get("Error").d("Error"),
          text: response.data?.detail || intl.get("FailedToReject").d("Failed to reject"),
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: intl.get("FailedToReject").d("Failed to reject"),
      });
    } finally {
      setActionLoading(false);
    }
  }, [rejectReason, selectedAccount, rejectAllMode, selectedInstitutionForReject, executeRejectAccount, fetchUserInstitutions, refreshTable, handleCloseDetailModal]);

  const handleApproveAtInstitution = useCallback(async (institutionPk, institutionName) => {
    try {
      setActionLoading(true);
      const response = await fetchData(
        `/account/admin/accounts/${selectedAccount.person_pk}/institutions/${institutionPk}/approve`,
        "POST",
        true
      );
      if (response.status === HTTP_STATUS.OK_200) {
        Swal.fire({
          icon: "success",
          title: intl.get("ApprovedAtInstitution").d("Approved at Institution"),
          text: intl.get("ApprovedAtInstitutionMessage", { name: institutionName }).d("User approved at {name}"),
        });
        await fetchUserInstitutions(selectedAccount.person_pk);
        refreshTable();
      } else {
        Swal.fire({
          icon: "error",
          title: intl.get("Error").d("Error"),
          text: response.data?.detail || intl.get("FailedToApprove").d("Failed to approve"),
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: intl.get("FailedToApprove").d("Failed to approve"),
      });
    } finally {
      setActionLoading(false);
    }
  }, [selectedAccount, fetchUserInstitutions, refreshTable]);

  const handleRejectAtInstitution = useCallback((institutionPk) => {
    setSelectedInstitutionForReject(institutionPk);
    setRejectAllMode(false);
    setRejectReason("");
    setRejectModalOpen(true);
  }, []);

  const handleApproveAllInstitutions = useCallback(async () => {
    // Filter institutions by approvability
    // If user cannot approve this person (institution_responsible only), reject the action
    if (selectedAccount.can_current_user_approve === false) {
      Swal.fire({
        icon: 'error',
        title: intl.get("Error").d("Error"),
        text: intl.get("YouCannotApproveThisUser").d("You cannot approve this user")
      });
      return;
    }

    try {
      setActionLoading(true);
      const response = await fetchData(
        `/account/admin/accounts/${selectedAccount.person_pk}/approve-all-institutions`,
        "POST",
        true
      );
      if (response.status === HTTP_STATUS.OK_200) {
        const results = response.data?.results || [];
        const successCount = results.filter(r => r.success).length;
        Swal.fire({
          icon: "success",
          title: intl.get("ApprovedAllInstitutions").d("Approved All Institutions"),
          text: intl.get("ApprovedAllInstitutionsMessage", { count: successCount }).d("User approved at {count} institution(s)"),
        });
        await fetchUserInstitutions(selectedAccount.person_pk);
        refreshTable();
      } else {
        Swal.fire({
          icon: "error",
          title: intl.get("Error").d("Error"),
          text: response.data?.detail || intl.get("FailedToApprove").d("Failed to approve"),
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: intl.get("FailedToApprove").d("Failed to approve"),
      });
    } finally {
      setActionLoading(false);
    }
  }, [selectedAccount, fetchUserInstitutions, refreshTable]);

  const handleRejectAllInstitutions = useCallback(() => {
    if (selectedAccount.can_current_user_approve === false) {
      Swal.fire({
        icon: 'error',
        title: intl.get("Error").d("Error"),
        text: intl.get("YouCannotRejectThisUser").d("You cannot reject this user")
      });
      return;
    }

    setSelectedInstitutionForReject(null);
    // For type requests, keep rejectAllMode=false so executeRejectAccount uses the correct endpoint
    setRejectAllMode(!selectedAccount.is_type_request);
    setRejectReason("");
    setRejectModalOpen(true);
  }, [selectedAccount]);

  const handlePreviewDocument = useCallback((doc) => {
    setPreviewDocument(doc);
    setPreviewModalOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewModalOpen(false);
    setPreviewDocument(null);
  }, []);

  const handleApproveUser = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      setActionLoading(true);
      const response = await fetchData(
        `/account/admin/accounts/${selectedAccount.person_pk}/approve-user`,
        "POST",
        true
      );
      if (response.status === HTTP_STATUS.OK_200) {
        const userName = response.data?.user_name || selectedAccount.user_name;
        Swal.fire({
          icon: "success",
          title: intl.get("Approval.UserApproved").d("User Approved"),
          text: intl.get("Approval.UserApprovedMessage", { name: userName }).d(`User ${userName} approved successfully. Now approve the institutions.`),
        });
        setSelectedAccount(prev => ({ ...prev, user_approved: true }));
        refreshTable();
      } else {
        Swal.fire({
          icon: "error",
          title: intl.get("Error").d("Error"),
          text: response.data?.detail || intl.get("FailedToApprove").d("Failed to approve"),
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: intl.get("Error").d("Error"),
        text: err?.response?.data?.detail || intl.get("FailedToApprove").d("Failed to approve"),
      });
    } finally {
      setActionLoading(false);
    }
  }, [selectedAccount, refreshTable]);

  const getTableUrl = useCallback(() => {
    return "/account/admin/pending-approvals/table";
  }, []);

  /**
   * Check if user is inactive (no login for X days or never logged in)
   */
  const isUserInactive = (row) => {
    // If user never logged in, check days since account creation
    if (row.days_since_login === null) {
      return row.days_since_creation !== null && row.days_since_creation >= INACTIVE_DAYS_THRESHOLD;
    }
    return row.days_since_login >= INACTIVE_DAYS_THRESHOLD;
  };

  /**
   * Check if user has no documents uploaded
   * Doctors don't need to upload documents, so exclude them
   */
  const hasNoDocuments = (row) => {
    if (row.user_type === "doctor") {
      return false;
    }
    return row.documents_count === 0;
  };

  const header = [
    {
      label: intl.get("Name").d("Name"),
      column_name: "user_name",
      filter: true,
      element: (row) => {
        const inactive = isUserInactive(row);
        const noDocuments = hasNoDocuments(row);
        const daysInfo = row.days_since_login !== null
          ? row.days_since_login
          : row.days_since_creation;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            {inactive && (
              <Tooltip
                title={intl.get("UserInactiveWarning", { days: daysInfo }).d("User has not logged in for {days} days")}
                arrow
              >
                <WarningIcon color="warning" sx={{ cursor: 'help', fontSize: '1.2rem' }} />
              </Tooltip>
            )}
            {noDocuments && (
              <Tooltip
                title={intl.get("NoDocumentsWarning").d("User has not uploaded any documents")}
                arrow
              >
                <WarningIcon color="error" sx={{ cursor: 'help', fontSize: '1.2rem' }} />
              </Tooltip>
            )}
            <span>{row.user_name}</span>
            {row.is_type_request && (
              <Chip
                label={`${intl.get("NewProfile").d("Novo Perfil")}: ${row.user_type_label || row.user_type}`}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ ml: 1, height: 'auto', fontSize: '0.7rem', py: 0.25 }}
              />
            )}
          </Box>
        );
      }
    },
    {
      label: intl.get("Email").d("Email"),
      column_name: "user_email",
      filter: true,
      element: (row) => row.user_email
    },
    {
      label: intl.get("CPF").d("CPF"),
      column_name: "cpf",
      filter: true,
      element: (row) => row.cpf
    },
    {
      label: intl.get("CRM_CRF").d("CRM/CRF"),
      column_name: "professional_registration",
      filter: true,
      element: (row) => {
        if (!row.professional_registration) return '-';
        const state = row.professional_registration_state || '';
        return `${row.professional_registration}${state ? ` (${state})` : ''}`;
      }
    },
    {
      label: intl.get("UserType").d("User Type"),
      column_name: "user_type",
      filter: true,
      sortable: false,
      element: (row) => {
        // If this is a type request with existing types, show only approved types
        // The requested type is already shown as a chip in the name column
        if (row.is_type_request && row.existing_types && row.existing_types.length > 0) {
          const approvedTypes = row.existing_types.filter(
            existingType => existingType.type !== row.user_type
          );

          if (approvedTypes.length > 0) {
            return (
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {approvedTypes.map((existingType, index) => {
                  let label = existingType.label;
                  if (existingType.type === "doctor") {
                    label = intl.get("Prescriber").d("Prescriber");
                  } else if (existingType.type === "pharmacist") {
                    label = intl.get("Dispenser").d("Dispenser");
                  } else if (existingType.type === "institution_responsible") {
                    label = intl.get("InstitutionResponsible").d("Institution Responsible");
                  }
                  return (
                    <Chip
                      key={index}
                      label={label}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            );
          }
        }

        // Default: single type display
        let typeLabel;
        if (row.user_type === "doctor") {
          typeLabel = intl.get("Prescriber").d("Prescriber");
        } else if (row.user_type === "pharmacist") {
          typeLabel = intl.get("Dispenser").d("Dispenser");
        } else if (row.user_type === "distributor_responsible") {
          typeLabel = intl.get("DistributorResponsible").d("Distributor Responsible");
        } else if (row.user_type === "institution_responsible" || row.user_type === "person") {
          typeLabel = intl.get("InstitutionResponsible").d("Institution Responsible");
        } else {
          typeLabel = row.user_type_label || row.user_type || intl.get("Unknown").d("Unknown");
        }
        return (
          <Chip
            label={typeLabel}
            size="small"
            color="secondary"
            variant="outlined"
          />
        );
      }
    },
    {
      label: intl.get("ApprovalStep").d("Approval Step"),
      column_name: "account_status_label",
      filter: false,
      sortable: false,
      element: (row) => (
        <Chip
          label={row.account_status_label || intl.get("Unknown").d("Unknown")}
          size="small"
          color="info"
          variant="outlined"
        />
      )
    },
    {
      label: intl.get("Documents").d("Documents"),
      column_name: "documents_count",
      filter: false,
      sortable: false,
      align: "center",
      element: (row) => (
        <Chip
          label={row.documents_count}
          size="small"
          color="primary"
          variant="outlined"
        />
      )
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      width: "100px",
      sortable: false,
      filter: false,
      element: (row) => {
        return (
          <DmsButtonGroup>
            <Tooltip title={intl.get("ViewDetails").d("View Details")}>
              <IconButton size="small" onClick={() => handleViewDetails(row)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </DmsButtonGroup>
        );
      }
    },
  ];

  return (
    <DmsPage name={intl.get("PendingAccountApprovals").d("Pending Account Approvals")} manual_slug="pending-approvals">
      <DmsAltBox.Card>
        <DmsAltBox.Header>
          <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
            {intl.get("PendingAccountApprovals").d("Pending Account Approvals")}
          </Typography>
        </DmsAltBox.Header>
        <DmsAltBox.Body>
          <DmsTable
            header={header}
            managed={true}
            needs_authentication={true}
            ref={tableRef}
            url={getTableUrl()}
            export_table={false}
          />

          {/* Warning Icons Legend */}
          <Box sx={{ display: 'flex', gap: 3, mt: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningIcon color="warning" sx={{ fontSize: '1.2rem' }} />
              <Typography variant="caption" color="text.secondary">
                {intl.get("InactiveUserLegend", { days: INACTIVE_DAYS_THRESHOLD }).d("User inactive for {days}+ days")}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WarningIcon color="error" sx={{ fontSize: '1.2rem' }} />
              <Typography variant="caption" color="text.secondary">
                {intl.get("NoDocumentsLegend").d("User has not uploaded documents")}
              </Typography>
            </Box>
          </Box>
        </DmsAltBox.Body>
      </DmsAltBox.Card>

      {/* Account Details Modal */}
      <DmsModal
        isVisible={detailModalOpen}
        onClose={handleCloseDetailModal}
        maxWidth="md"
        fullWidth
      >
        <DmsModal.Header title={intl.get("AccountDetails").d("Account Details")} />
        <DmsModal.Body>
          {selectedAccount && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedAccount.user_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedAccount.user_email} | CPF: {selectedAccount.cpf}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {selectedAccount.is_type_request ? (
                  <>
                    {/* Approved types */}
                    {(selectedAccount.existing_types || [])
                      .filter(existingType => existingType.type !== selectedAccount.user_type)
                      .map((existingType, index) => {
                        let label = existingType.label;
                        if (existingType.type === "doctor") {
                          label = intl.get("Prescriber").d("Prescriber");
                        } else if (existingType.type === "pharmacist") {
                          label = intl.get("Dispenser").d("Dispenser");
                        } else if (existingType.type === "institution_responsible") {
                          label = intl.get("InstitutionResponsible").d("Institution Responsible");
                        }
                        return (
                          <Chip
                            key={index}
                            label={label}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        );
                      })
                    }
                    {/* New requested profile chip */}
                    <Chip
                      label={`${intl.get("NewProfile").d("Novo Perfil")}: ${selectedAccount.user_type_label || selectedAccount.user_type}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ fontStyle: 'italic' }}
                    />
                  </>
                ) : (
                  <Chip
                    label={
                      selectedAccount.user_type === "doctor"
                        ? intl.get("Prescriber").d("Prescriber")
                        : selectedAccount.user_type === "pharmacist"
                          ? intl.get("Dispenser").d("Dispenser")
                          : selectedAccount.user_type === "distributor_responsible"
                            ? intl.get("DistributorResponsible").d("Distributor Responsible")
                            : selectedAccount.user_type === "institution_responsible" || selectedAccount.user_type === "person"
                              ? intl.get("InstitutionResponsible").d("Institution Responsible")
                              : selectedAccount.user_type_label || selectedAccount.user_type || intl.get("Unknown").d("Unknown")
                    }
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Registration Information */}
              <Typography variant="subtitle1" gutterBottom>
                {intl.get("Approval.RegistrationInfo").d("Registration Information")}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Professional Registration (CRM/CRF) */}
                {selectedAccount.professional_registration && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {intl.get("Approval.ProfessionalRegistration").d("Professional Registration")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedAccount.user_type === "doctor"
                          ? intl.get("CRM").d("CRM")
                          : intl.get("CRF").d("CRF")}
                      </Typography>
                      <Typography variant="body1">
                        {selectedAccount.professional_registration}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        {intl.get("State").d("State")}
                      </Typography>
                      <Typography variant="body1">
                        {selectedAccount.professional_registration_state || intl.get("NotProvided").d("Not Provided")}
                      </Typography>
                    </Grid>
                  </>
                )}

                {/* Phone */}
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {intl.get("Phone").d("Phone")}
                  </Typography>
                  <Typography variant="body1">
                    {selectedAccount.user_phone || intl.get("NotProvided").d("Not Provided")}
                  </Typography>
                </Grid>

                {/* Address */}
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {intl.get("Address").d("Address")}
                  </Typography>
                  {selectedAccount.address_street ? (
                    <Typography variant="body1">
                      {selectedAccount.address_street}{selectedAccount.address_number ? `, ${selectedAccount.address_number}` : ""}
                      {" - "}
                      {selectedAccount.address_city}{selectedAccount.address_state ? ` / ${selectedAccount.address_state}` : ""}
                      {selectedAccount.address_zip_code ? ` - ${intl.get("CEP").d("Zip Code")}: ${selectedAccount.address_zip_code}` : ""}
                    </Typography>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      {intl.get("NotProvided").d("Not Provided")}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {selectedAccount && selectedAccount.user_type !== "doctor" && (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    {intl.get("UploadedDocuments").d("Uploaded Documents")}
                  </Typography>

                  {loadingDocuments ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : accountDocuments.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {intl.get("NoDocumentsUploaded").d("No documents uploaded")}
                    </Alert>
                  ) : hasMultipleInstitutions ? (
                    // Documents grouped by institution
                    <Box>
                      {Object.entries(documentsByInstitution).map(([key, group]) => {
                        const isInactiveInst = group.institution_pk && institutionStatusMap[group.institution_pk] === InstitutionStatusEnum.INACTIVE;
                        return (
                        <Box key={key} sx={{ mb: 3 }}>
                          {group.institution_name ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                              <BusinessIcon color={isInactiveInst ? "disabled" : "primary"} fontSize="small" />
                              <Typography variant="subtitle2" color={isInactiveInst ? "text.secondary" : "primary.main"} fontWeight={600}>
                                {group.institution_name}
                                {isInactiveInst && ` - ${intl.get("InactiveInstitution").d("Instituição Inativa")}`}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                              {intl.get("DocumentsWithoutInstitution").d("Documents without institution")}
                            </Typography>
                          )}
                          <List dense disablePadding>
                            {group.documents.map((doc) => (
                              <ListItem
                                key={doc.pk}
                                sx={{
                                  bgcolor: "background.paper",
                                  borderRadius: 1,
                                  mb: 1,
                                  border: "1px solid",
                                  borderColor: "divider",
                                  ml: group.institution_name ? 2 : 0,
                                }}
                                secondaryAction={
                                  <Box sx={{ display: "flex", gap: 0.5 }}>
                                    <Tooltip title={intl.get("Preview").d("Preview")}>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handlePreviewDocument(doc)}
                                        disabled={!doc.file_pk}
                                      >
                                        <VisibilityIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={intl.get("Download").d("Download")}>
                                      <IconButton
                                        size="small"
                                        onClick={() => downloadFile(doc.file_pk)}
                                        disabled={!doc.file_pk}
                                      >
                                        <DownloadIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                }
                              >
                                <ListItemIcon>
                                  <DescriptionIcon color="action" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={doc.document_type_label}
                                  secondary={
                                    doc.created_on && (
                                      <Typography variant="caption" color="text.secondary">
                                        {intl.get("UploadedOn").d("Uploaded on")}: {new Date(doc.created_on).toLocaleDateString('pt-BR')}
                                      </Typography>
                                    )
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )})}
                    </Box>
                  ) : (
                    // Simple list when no institution grouping
                    <List dense>
                      {accountDocuments.map((doc) => (
                          <ListItem
                            key={doc.pk}
                            sx={{
                              bgcolor: "background.paper",
                              borderRadius: 1,
                              mb: 1,
                              border: "1px solid",
                              borderColor: "divider",
                            }}
                            secondaryAction={
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Tooltip title={intl.get("Preview").d("Preview")}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handlePreviewDocument(doc)}
                                    disabled={!doc.file_pk}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={intl.get("Download").d("Download")}>
                                  <IconButton
                                    size="small"
                                    onClick={() => downloadFile(doc.file_pk)}
                                    disabled={!doc.file_pk}
                                  >
                                    <DownloadIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            }
                          >
                            <ListItemIcon>
                              <DescriptionIcon color="action" />
                            </ListItemIcon>
                            <ListItemText
                              primary={doc.document_type_label}
                              secondary={
                                doc.created_on && (
                                  <Typography variant="caption" color="text.secondary">
                                    {intl.get("UploadedOn").d("Uploaded on")}: {new Date(doc.created_on).toLocaleDateString('pt-BR')}
                                  </Typography>
                                )
                              }
                            />
                          </ListItem>
                      ))}
                    </List>
                  )}

                  <Divider sx={{ my: 2 }} />
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {selectedAccount.is_type_request ? (
                /* New Profile Approval Section */
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    {intl.get("Approval.ApproveNewProfile").d("Approve New Profile")}
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {intl.get("Approval.ApproveNewProfileInfo").d("This user already has an active account. Approve or reject the request to add a new professional profile.")}
                  </Alert>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <DmsButton
                      variant="danger"
                      text={intl.get("Reject").d("Reject")}
                      onClick={handleRejectAllInstitutions}
                      disabled={actionLoading}
                    />
                    <DmsButton
                      variant="primary"
                      text={intl.get("Approval.ApproveNewProfile").d("Approve New Profile")}
                      onClick={() => {
                        setAccountToApprove(selectedAccount);
                        setApproveModalOpen(true);
                      }}
                      disabled={actionLoading}
                    />
                  </Box>
                </Box>
              ) : (
                <>
                  {/* User Approval Section */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {intl.get("Approval.ApproveUser").d("Approve User")}
                    </Typography>
                    {selectedAccount.user_approved ? (
                      <Chip
                        icon={<VerifiedUserIcon />}
                        label={intl.get("Approval.UserApproved").d("User Approved")}
                        color="success"
                        sx={{ mb: 1 }}
                      />
                    ) : (
                      <>
                        <Alert severity="info" sx={{ mb: 1 }}>
                          {intl.get("Approval.ApproveUserFirst").d("Approve the user before approving institutions.")}
                        </Alert>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <DmsButton
                            variant="danger"
                            text={intl.get("Approval.RejectUser").d("Reject User")}
                            onClick={handleRejectAllInstitutions}
                            disabled={actionLoading}
                          />
                          <DmsButton
                            variant="primary"
                            text={intl.get("Approval.ApproveUser").d("Approve User")}
                            onClick={handleApproveUser}
                            disabled={actionLoading}
                          />
                        </Box>
                      </>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" gutterBottom>
                    {intl.get("Institutions").d("Institutions")}
                  </Typography>

              {loadingInstitutions ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : userInstitutions.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {intl.get("NoInstitutions").d("User is not linked to any institutions")}
                </Alert>
              ) : (
                <>
                  <List dense>
                    {userInstitutions.map((inst) => {
                      const isApproved = inst.is_approved;
                      const isRejected = inst.status === 6;  // AccountStatusEnum.REJECTED
                      const isInactiveInstitution = inst.institution_status === InstitutionStatusEnum.INACTIVE;
                      // Only show approve/reject buttons for statuses that actually need approval
                      // PENDING_TRAINING (4) is already approved, just waiting for training
                      // Don't show buttons for inactive institutions
                      const needsApproval = selectedAccount.user_approved && !isInactiveInstitution && (inst.status === 8 || inst.status === 3 || inst.status === 9);  // PENDING_APPROVAL_GESTAO_LENA, PENDING_APPROVAL, PENDING_APPROVAL_REGULATORIO

                      return (
                        <ListItem
                          key={inst.institution_pk}
                          sx={{
                            bgcolor: "background.paper",
                            borderRadius: 1,
                            mb: 1,
                            border: "1px solid",
                            borderColor: isInactiveInstitution ? "grey.300" : isApproved ? "success.light" : isRejected ? "error.light" : "divider",
                          }}
                          secondaryAction={
                            needsApproval && approvalPermissions.can_approve_gestao_lena ? (
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Tooltip title={intl.get("Approve").d("Approve")}>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    onClick={() => handleApproveAtInstitution(inst.institution_pk, inst.institution_name)}
                                    disabled={actionLoading}
                                  >
                                    <CheckCircleIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={intl.get("Reject").d("Reject")}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRejectAtInstitution(inst.institution_pk)}
                                    disabled={actionLoading}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : null
                          }
                        >
                          <ListItemIcon>
                            <BusinessIcon color={isInactiveInstitution ? "disabled" : isApproved ? "success" : isRejected ? "error" : "action"} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              (inst.institution_name || `Institution ${inst.institution_pk}`) +
                              (isInactiveInstitution ? ` - ${intl.get("InactiveInstitution").d("Instituição Inativa")}` : '')
                            }
                            secondary={
                              <Box>
                                <Chip
                                  label={isInactiveInstitution
                                    ? intl.get("InactiveInstitution").d("Instituição Inativa")
                                    : (inst.status_label || intl.get("Unknown").d("Unknown"))}
                                  size="small"
                                  color={
                                    isInactiveInstitution
                                      ? "default"
                                      : isApproved
                                        ? "success"
                                        : isRejected
                                          ? "error"
                                          : inst.status === 4
                                            ? "warning"  // PENDING_TRAINING - approved but awaiting training
                                            : "default"
                                  }
                                  sx={{ mt: 0.5 }}
                                />
                                {isRejected && inst.status_reason && (
                                  <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                                    {intl.get("Reason").d("Reason")}: {inst.status_reason}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>

                  {selectedAccount.user_approved && approvalPermissions.can_approve_gestao_lena && userInstitutions.some(i => i.status === 8 || i.status === 3 || i.status === 9) && (
                    <Box sx={{ mt: 2, display: "flex", gap: 1, justifyContent: "flex-end" }}>
                      <DmsButton
                        variant="danger"
                        text={intl.get("Approval.RejectUser").d("Reject User")}
                        onClick={handleRejectAllInstitutions}
                        disabled={actionLoading}
                      />
                      <DmsButton
                        variant="primary"
                        text={intl.get("ApproveAll").d("Approve All")}
                        onClick={handleApproveAllInstitutions}
                        disabled={actionLoading}
                      />
                    </Box>
                  )}
                </>
              )}
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <HistoryIcon color="action" />
                <Typography variant="subtitle1">
                  {intl.get("ApprovalHistory").d("Approval History")}
                </Typography>
              </Box>

              {loadingHistory ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : approvalHistory.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {intl.get("NoApprovalHistory").d("No approval history yet")}
                </Alert>
              ) : (
                <List dense>
                  {approvalHistory.map((item, index) => (
                    <ListItem
                      key={item.pk || index}
                      sx={{
                        bgcolor: "background.paper",
                        borderRadius: 1,
                        mb: 1,
                        border: "1px solid",
                        borderColor: item.is_approval ? "success.light" : "error.light",
                        borderLeft: "4px solid",
                        borderLeftColor: item.is_approval ? "success.main" : "error.main",
                      }}
                    >
                      <ListItemIcon>
                        {item.is_approval ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {item.approver_role === "EUROFARMA_GESTAO_LENA"
                                ? intl.get("GestaoLena").d("Gestão Lena")
                                : item.approver_role === "EUROFARMA_REGULATORIO"
                                  ? intl.get("Regulatorio").d("Regulatório")
                                  : item.approver_role === "EUROFARMA_DIRETORIA"
                                    ? intl.get("Diretoria").d("Diretoria")
                                    : item.approver_role || intl.get("Unknown").d("Unknown")
                              }
                            </Typography>
                            <Chip
                              size="small"
                              label={item.is_approval
                                ? intl.get("Approved").d("Approved")
                                : intl.get("Rejected").d("Rejected")
                              }
                              color={item.is_approval ? "success" : "error"}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {intl.get("By").d("By")}: {item.action_by_name || "-"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {intl.get("Date").d("Date")}: {item.action_on ? new Date(item.action_on).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : "-"}
                            </Typography>
                            {item.justification && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {intl.get("Justification").d("Justification")}: {item.justification}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {selectedAccount?.account_status_label && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1, borderLeft: "4px solid", borderLeftColor: "primary.main" }}>
                  <Typography variant="body2">
                    <strong>{intl.get("CurrentStep").d("Current Step")}:</strong> {selectedAccount.account_status_label}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DmsModal.Body>
      </DmsModal>

      {/* Rejection Modal */}
      <DmsModal
        isVisible={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DmsModal.Header title={intl.get("RejectAccount").d("Reject Account")} />
        <DmsModal.Body>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" gutterBottom>
              {intl.get("RejectAccountMessage").d("Please provide a reason for rejecting this account:")}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={intl.get("EnterRejectionReason").d("Enter rejection reason...")}
              sx={{ mt: 2 }}
            />
          </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
            <DmsButton
              variant="secondary"
              text={intl.get("Cancel").d("Cancel")}
              onClick={() => setRejectModalOpen(false)}
            />
            <DmsButton
              variant="danger"
              text={intl.get("Reject").d("Reject")}
              onClick={handleRejectAccount}
              disabled={actionLoading || !rejectReason.trim()}
            />
          </Box>
        </DmsModal.Footer>
      </DmsModal>

      {/* Approval Confirmation Modal */}
      <DmsModal
        isVisible={approveModalOpen}
        onClose={() => {
          setApproveModalOpen(false);
          setAccountToApprove(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DmsModal.Header title={intl.get("ConfirmApproval").d("Confirm Approval")} />
        <DmsModal.Body>
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" gutterBottom>
              {intl.get("ConfirmApprovalMessage").d("Are you sure you want to approve this user?")}
            </Typography>
            {accountToApprove && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{intl.get("Name").d("Name")}:</strong> {accountToApprove.user_name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{intl.get("Email").d("Email")}:</strong> {accountToApprove.user_email}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{intl.get("CPF").d("CPF")}:</strong> {accountToApprove.cpf}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{intl.get("UserType").d("User Type")}:</strong>{" "}
                    {accountToApprove.user_type === "doctor"
                      ? intl.get("Prescriber").d("Prescriber")
                      : accountToApprove.user_type === "pharmacist"
                        ? intl.get("Dispenser").d("Dispenser")
                        : accountToApprove.user_type === "distributor_responsible"
                          ? intl.get("DistributorResponsible").d("Distributor Responsible")
                          : intl.get("InstitutionResponsible").d("Institution Responsible")}
                  </Typography>
                </Box>
                {accountToApprove.account_status_label && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1, borderLeft: "4px solid", borderLeftColor: "primary.main" }}>
                    <Typography variant="body2">
                      <strong>{intl.get("CurrentStep").d("Current Step")}:</strong> {accountToApprove.account_status_label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {intl.get("ApprovalStepMessage").d("Approving this step will advance to the next phase of the approval flow.")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {intl.get("ApprovalConsequence").d("After approval, the user will be able to access the system.")}
            </Typography>
          </Box>
        </DmsModal.Body>
        <DmsModal.Footer>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
            <DmsButton
              variant="secondary"
              text={intl.get("Cancel").d("Cancel")}
              onClick={() => {
                setApproveModalOpen(false);
                setAccountToApprove(null);
              }}
            />
            <DmsButton
              variant="primary"
              text={intl.get("Approve").d("Approve")}
              onClick={executeApproveAccount}
              disabled={actionLoading}
            />
          </Box>
        </DmsModal.Footer>
      </DmsModal>

      {/* Document Preview Modal */}
      <DmsFileViewerModal
        isOpen={previewModalOpen}
        onClose={handleClosePreview}
        filePk={previewDocument?.file_pk}
        fileName={previewDocument?.file?.original_filename}
        title={previewDocument?.document_type_label}
      />
    </DmsPage>
  );
};
